import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { resizeImage } from '../../lib/image';

const API = import.meta.env.VITE_API_BASE_URL || '/api';
const emptyAddr = () => ({ line1: '', line2: '', city: '', state: '', postal: '', floor: '' });

export default function MoveQuestionnaire({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const fileRefs = useRef({});

  const fileUrl = (id) => `${API}/move/${token}/media/${id}/file`;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await api(`/move/${token}`);
      setData(d);
      setForm({
        simulator_type: d.simulator_type || '', serial_number: d.serial_number || '', year_purchased: d.year_purchased || '',
        move_from: { ...emptyAddr(), ...(d.move_from || {}) }, move_to: { ...emptyAddr(), ...(d.move_to || {}) },
        new_room_size: d.new_room_size || '',
        doorways_36_certified: !!d.doorways_36_certified, certified_by: d.certified_by || '',
        move_info: d.move_info || '', additional_work: d.additional_work || '',
      });
      if (d.status === 'submitted') setSubmitted(true);
    } catch (e) { setError(e?.detail || 'This questionnaire link is not valid or has expired.'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (which, k, v) => setForm((f) => ({ ...f, [which]: { ...f[which], [k]: v } }));

  function bodyFromForm() {
    return {
      simulator_type: form.simulator_type, serial_number: form.serial_number,
      year_purchased: form.year_purchased || null,
      move_from: form.move_from, move_to: form.move_to, new_room_size: form.new_room_size,
      doorways_36_certified: form.doorways_36_certified, certified_by: form.certified_by,
      move_info: form.move_info, additional_work: form.additional_work,
    };
  }
  async function save() {
    setBusy(true); setSavedMsg(null);
    try { const d = await api(`/move/${token}`, { method: 'PUT', body: bodyFromForm() }); setData(d); setSavedMsg('Saved.'); }
    catch (e) { setSavedMsg(e?.detail || 'Could not save.'); }
    finally { setBusy(false); }
  }
  async function submit() {
    if (!form.doorways_36_certified) { setSavedMsg('Please confirm the 36" doorway certification before submitting.'); return; }
    setBusy(true); setSavedMsg(null);
    try {
      await api(`/move/${token}`, { method: 'PUT', body: bodyFromForm() });
      await api(`/move/${token}/submit`, { method: 'POST', body: { submitted_by: form.certified_by } });
      setSubmitted(true); window.scrollTo(0, 0);
    } catch (e) { setSavedMsg(e?.detail || 'Could not submit.'); }
    finally { setBusy(false); }
  }

  const mediaOf = (cat) => (data?.media || []).filter((m) => m.category === cat);

  async function addPhotos(category, files) {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const image = await resizeImage(file);
        await api(`/move/${token}/photo`, { method: 'POST', body: { category, image } });
      }
      await load();
    } catch (e) { setSavedMsg(e?.message || 'Photo upload failed.'); }
    finally { setBusy(false); }
  }
  async function addVideoFile(category, file) {
    if (!file) return;
    setBusy(true); setSavedMsg('Uploading video…');
    try {
      const fd = new FormData(); fd.append('category', category); fd.append('video', file);
      const res = await fetch(`${API}/move/${token}/video`, { method: 'POST', body: fd });
      if (!res.ok) { const t = await res.text(); throw new Error(t || 'Upload failed'); }
      setSavedMsg(null); await load();
    } catch (e) { setSavedMsg('Video upload failed — for very large files, paste a share link instead.'); }
    finally { setBusy(false); }
  }
  async function addVideoUrl(category, url) {
    if (!url) return;
    setBusy(true);
    try { await api(`/move/${token}/video`, { method: 'POST', body: { category, url } }); await load(); }
    catch (e) { setSavedMsg(e?.detail || 'Could not add link.'); }
    finally { setBusy(false); }
  }
  async function removeMedia(id) {
    try { await api(`/move/${token}/media/${id}`, { method: 'DELETE' }); await load(); } catch { /* ignore */ }
  }

  if (loading) return <div className="move-wrap"><div className="move-card"><p>Loading…</p></div></div>;
  if (error) return <div className="move-wrap"><div className="move-card"><h1>Pre-move questionnaire</h1><p className="move-err">{error}</p></div></div>;

  const PhotoBlock = ({ category, label }) => (
    <div className="move-media">
      <div className="move-media__head">
        <span>{label}</span>
        <button type="button" className="btn btn--sm" disabled={busy} onClick={() => fileRefs.current[category]?.click()}>+ Add photos</button>
        <input ref={(el) => (fileRefs.current[category] = el)} type="file" accept="image/*" capture="environment" multiple
          style={{ display: 'none' }} onChange={(e) => { addPhotos(category, e.target.files); e.target.value = ''; }} />
      </div>
      <div className="move-thumbs">
        {mediaOf(category).map((m) => (
          <div className="move-thumb" key={m.id}>
            <img src={fileUrl(m.id)} alt="" />
            <button type="button" className="move-thumb__rm" onClick={() => removeMedia(m.id)}>×</button>
          </div>
        ))}
        {mediaOf(category).length === 0 && <span className="move-hint">No photos yet.</span>}
      </div>
    </div>
  );

  const VideoBlock = ({ category, label, help }) => {
    const [url, setUrl] = useState('');
    return (
      <div className="move-media">
        <div className="move-media__head"><span>{label}</span></div>
        <p className="move-hint">{help}</p>
        <div className="move-vids">
          {mediaOf(category).map((m) => (
            <div className="move-vid" key={m.id}>
              {m.storage === 'url'
                ? <a href={m.url} target="_blank" rel="noreferrer">Linked video ↗</a>
                : <video controls preload="metadata" src={fileUrl(m.id)} />}
              <div className="move-vid__meta">
                <span>{m.original_name || (m.storage === 'url' ? 'Link' : 'Video')}{m.size_bytes ? ` · ${(m.size_bytes / 1048576).toFixed(0)} MB` : ''}</span>
                <button type="button" className="btn btn--sm btn--ghost" onClick={() => removeMedia(m.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
        <div className="move-vid-add">
          <button type="button" className="btn btn--sm" disabled={busy} onClick={() => fileRefs.current[`v_${category}`]?.click()}>+ Upload video</button>
          <input ref={(el) => (fileRefs.current[`v_${category}`] = el)} type="file" accept="video/*" capture="environment"
            style={{ display: 'none' }} onChange={(e) => { addVideoFile(category, e.target.files?.[0]); e.target.value = ''; }} />
          <span className="move-or">or</span>
          <input className="move-url" placeholder="paste a share link (Dropbox, Drive, …)" value={url} onChange={(e) => setUrl(e.target.value)} />
          <button type="button" className="btn btn--sm" disabled={busy || !url} onClick={() => { addVideoUrl(category, url); setUrl(''); }}>Add link</button>
        </div>
      </div>
    );
  };

  return (
    <div className="move-wrap">
      <div className="move-card">
        <header className="move-head">
          <div className="move-head__biz">{data.business_name || 'Flightdeck'}</div>
          <h1>Simulator move — pre-move questionnaire</h1>
          <p className="move-sub">{data.job_number}{data.customer_name ? ` · ${data.customer_name}` : ''}</p>
        </header>

        {submitted && <div className="move-banner">Thanks — your questionnaire has been submitted. You can still update it below if anything changes.</div>}

        <section className="move-sec">
          <h2>Simulator</h2>
          <div className="move-grid">
            <label>Simulator type<input value={form.simulator_type} onChange={(e) => set('simulator_type', e.target.value)} placeholder="e.g. Cirrus SR22 AATD" /></label>
            <label>Serial number<input value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} /></label>
            <label>Year purchased<input type="number" value={form.year_purchased} onChange={(e) => set('year_purchased', e.target.value)} /></label>
          </div>
        </section>

        <section className="move-sec">
          <h2>Current location</h2>
          <div className="move-grid">
            <label className="move-col2">Street address<input value={form.move_from.line1} onChange={(e) => setAddr('move_from', 'line1', e.target.value)} /></label>
            <label>Suite / unit<input value={form.move_from.line2} onChange={(e) => setAddr('move_from', 'line2', e.target.value)} /></label>
            <label>City<input value={form.move_from.city} onChange={(e) => setAddr('move_from', 'city', e.target.value)} /></label>
            <label>State<input value={form.move_from.state} onChange={(e) => setAddr('move_from', 'state', e.target.value)} /></label>
            <label>ZIP<input value={form.move_from.postal} onChange={(e) => setAddr('move_from', 'postal', e.target.value)} /></label>
            <label>Floor level<input value={form.move_from.floor} onChange={(e) => setAddr('move_from', 'floor', e.target.value)} placeholder="e.g. Ground, 2nd" /></label>
          </div>
          <PhotoBlock category="existing_photo" label="Photos of the existing location" />
        </section>

        <section className="move-sec">
          <h2>New location</h2>
          <div className="move-grid">
            <label className="move-col2">Street address<input value={form.move_to.line1} onChange={(e) => setAddr('move_to', 'line1', e.target.value)} /></label>
            <label>Suite / unit<input value={form.move_to.line2} onChange={(e) => setAddr('move_to', 'line2', e.target.value)} /></label>
            <label>City<input value={form.move_to.city} onChange={(e) => setAddr('move_to', 'city', e.target.value)} /></label>
            <label>State<input value={form.move_to.state} onChange={(e) => setAddr('move_to', 'state', e.target.value)} /></label>
            <label>ZIP<input value={form.move_to.postal} onChange={(e) => setAddr('move_to', 'postal', e.target.value)} /></label>
            <label>Floor level<input value={form.move_to.floor} onChange={(e) => setAddr('move_to', 'floor', e.target.value)} placeholder="e.g. Ground, 2nd" /></label>
            <label className="move-col2">Room size of the new location<input value={form.new_room_size} onChange={(e) => set('new_room_size', e.target.value)} placeholder="e.g. 20 ft x 24 ft, 9 ft ceiling" /></label>
          </div>
          <PhotoBlock category="new_photo" label="Photos of the new location" />
        </section>

        <section className="move-sec move-sec--cert">
          <h2>Doorway clearance</h2>
          <label className="move-check">
            <input type="checkbox" checked={form.doorways_36_certified} onChange={(e) => set('doorways_36_certified', e.target.checked)} />
            <span>I certify that every doorway the simulator must pass through is at least <strong>36 inches wide</strong>, measured as the inside (clear) width of the door jamb.</span>
          </label>
          <label className="move-certby">Your name (certification)<input value={form.certified_by} onChange={(e) => set('certified_by', e.target.value)} placeholder="Full name" /></label>
        </section>

        <section className="move-sec">
          <h2>Path videos</h2>
          <VideoBlock category="removal_video" label="Removal path video"
            help="Walk the route the simulator will take to get OUT of the current building — from the sim's room to the exit." />
          <VideoBlock category="install_video" label="Install path video"
            help="Walk the route the simulator will take to get INTO the new location — from the entrance to the destination room." />
        </section>

        <section className="move-sec">
          <h2>Additional information</h2>
          <label className="move-block">About the move<textarea rows={3} value={form.move_info} onChange={(e) => set('move_info', e.target.value)} placeholder="Access notes, elevators, parking, stairs, timing constraints…" /></label>
          <label className="move-block">Additional work needed<textarea rows={3} value={form.additional_work} onChange={(e) => set('additional_work', e.target.value)} placeholder="Anything you'd like done as part of this move" /></label>
        </section>

        <footer className="move-foot">
          {savedMsg && <span className="move-saved">{savedMsg}</span>}
          <button className="btn" disabled={busy} onClick={save}>Save draft</button>
          <button className="btn btn--primary" disabled={busy} onClick={submit}>Submit questionnaire</button>
        </footer>
      </div>
    </div>
  );
}
